import React, { useState, useEffect } from "react";
import {
  Table,
  Tag,
  Input,
  Button,
  DatePicker,
  Select,
  Card,
  Tooltip,
  Space,
  Badge,
  message,
  Alert,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  ReloadOutlined,
  CalendarOutlined,
  FilePdfOutlined,
  EditOutlined,
  DeleteOutlined,
  BarChartOutlined,
} from "@ant-design/icons";
import locale from "antd/es/date-picker/locale/es_ES";
import icono_excel from "../../assets/img/icono_excel.png";
import FormNovedad from "./FormNovedad";
import ReporteNovedadesModal from "./ReporteNovedadesModal";
import { novedadesApi, tiposNovedadesApi } from "../../services/api";
import { toast } from "react-toastify";
import moment from "moment";
import DeleteConfirmModal from "../DeleteConfirmModal";
import { usePermissions, ROLES, PERMISSIONS } from "../../hooks/usePermissions";
import {
  PermissionGuard,
  AdminAndStaff,
  AdminOnly,
  AdminAndCoordinator,
} from "../PermissionGuard";

const { RangePicker } = DatePicker;
const { Option } = Select;

const TablaNovedadesConPermisos = () => {
  const [formVisible, setFormVisible] = useState(false);
  const [reporteVisible, setReporteVisible] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const [campus, setCampus] = useState(null);
  const [tipo, setTipo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [novedades, setNovedades] = useState([]);
  const [tiposNovedades, setTiposNovedades] = useState([]);
  const [campusOptions, setCampusOptions] = useState([]);
  const [editingNovedad, setEditingNovedad] = useState(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [novedadToDelete, setNovedadToDelete] = useState(null);

  // Hook de permisos
  const { canManageData, isReadOnly, hasPermission, userRole, roleName } =
    usePermissions();

  // Cargar datos
  useEffect(() => {
    cargarDatos();
  }, []);
  const cargarDatos = async () => {
    setLoading(true);
    try {
      const [novedadesRes, tiposRes] = await Promise.all([
        novedadesApi.getAll(),
        tiposNovedadesApi.getAll(),
      ]);

      console.log("Datos de novedades recibidos:", novedadesRes);

      // Procesar datos de novedades según la estructura de la respuesta
      let novedadesData = [];
      if (novedadesRes.data && novedadesRes.data.novedades) {
        // El dato está en data.novedades
        novedadesData = Array.isArray(novedadesRes.data.novedades)
          ? novedadesRes.data.novedades
          : [];
      } else if (Array.isArray(novedadesRes.data)) {
        // El dato está directamente en data como array
        novedadesData = novedadesRes.data;
      } else if (novedadesRes.data && novedadesRes.data.novedad) {
        // Es un solo registro en data.novedad
        novedadesData = [novedadesRes.data.novedad];
      }
      console.log("Novedades procesadas:", novedadesData);

      // Extraer opciones de campus de las novedades
      const campusSet = new Set();
      novedadesData.forEach((novedad) => {
        const campusValue = novedad.empleado?.campus?.campus;
        if (campusValue && campusValue !== "N/A") {
          campusSet.add(campusValue);
        }
      });
      setCampusOptions(Array.from(campusSet));

      setNovedades(novedadesData);
      let tiposData = [];
      if (tiposRes.data && tiposRes.data.tipos_novedades) {
 
        tiposData = tiposRes.data.tipos_novedades;
      } else if (Array.isArray(tiposRes.data)) {
        tiposData = tiposRes.data;
      }

      setTiposNovedades(tiposData);
    } catch (error) {
      console.error("Error al cargar datos:", error);
      toast.error("Error al cargar los datos");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record) => {
    if (!canManageData()) {
      message.warning("No tienes permisos para editar novedades");
      return;
    }
    setEditingNovedad(record);
    setFormVisible(true);
  };

  const handleDelete = (record) => {
    if (!canManageData()) {
      message.warning("No tienes permisos para eliminar novedades");
      return;
    }
    setNovedadToDelete(record);
    setDeleteModalVisible(true);
  };

  const confirmDelete = async () => {
    try {
      await novedadesApi.delete(novedadToDelete.id);
      toast.success("Novedad eliminada exitosamente");
      cargarDatos();
      setDeleteModalVisible(false);
      setNovedadToDelete(null);
    } catch (error) {
      console.error("Error al eliminar novedad:", error);
      toast.error("Error al eliminar la novedad");
    }
  };

  // Función para visualizar el soporte de una novedad
  const handleViewSupport = async (id) => {
    try {
      const response = await novedadesApi.getFile(id);

      // Crear una URL para el blob
      const url = window.URL.createObjectURL(response.data);

      // Abrir en una nueva pestaña
      window.open(url, "_blank");
    } catch (error) {
      console.error("Error al obtener el soporte:", error);
      toast.error("No se pudo cargar el archivo de soporte", {
        position: "top-right",
        autoClose: 4000,
      });
    }
  };
  const handleFormSubmit = async (values) => {
    try {
      if (editingNovedad) {
        await novedadesApi.update(editingNovedad.id, values);
        toast.success("Novedad actualizada exitosamente");
      } else {
        await novedadesApi.create(values);
        toast.success("Novedad creada exitosamente");
      }
      cargarDatos();
      setFormVisible(false);
      setEditingNovedad(null);
    } catch (error) {
      console.error("Error al guardar novedad:", error);
      toast.error("Error al guardar la novedad");
    }
  };
  const getTableColumns = () => {
    const baseColumns = [
      {
        title: (
          <div className="flex items-center justify-center">
            <span className="font-medium">Campus</span>
          </div>
        ),
        dataIndex: ["empleado", "campus", "campus"],
        key: "campus",
        width: 90,
        filters: campusOptions.map((c) => ({ text: c, value: c })),
        onFilter: (value, record) => record.empleado?.campus?.campus === value,
        align: "center",
        responsive: ["xs", "sm", "md", "lg"],
        render: (text) => (
          <Badge
            status={text === "BGA" ? "processing" : "warning"}
            text={text || "N/A"}
          />
        ),
      },
      {
        title: (
          <div className="flex items-center">
            <CalendarOutlined className="mr-1" />
            <span className="font-medium">Fecha Novedad</span>
          </div>
        ),
        dataIndex: "fecha_ini",
        key: "fecha_ini",
        width: 120,
        sorter: (a, b) => moment(a.fecha_ini).unix() - moment(b.fecha_ini).unix(),
        sortDirections: ["descend", "ascend"],
        responsive: ["sm", "md", "lg"],
        render: (fecha_ini) => (
          <div className="text-center">
            {moment(fecha_ini).format("DD/MM/YYYY")}
          </div>
        ),
      },
      {
        title: (
          <div className="flex items-center">
            <span className="font-medium">Nombre Empleado</span>
          </div>
        ),
        dataIndex: ["empleado", "nombre"],
        key: "empleado",
        ellipsis: true,
        width: 150,
        responsive: ["sm", "md", "lg"],
        sorter: (a, b) => a.empleado?.nombre?.localeCompare(b.empleado?.nombre),
      },
      {
        title: (
          <div className="flex items-center">
            <span className="font-medium">Cédula</span>
          </div>
        ),
        dataIndex: ["empleado", "cedula"],
        key: "cedula",
        width: 120,
        align: "right",
        responsive: ["md", "lg"],
      },
      {
        title: (
          <div className="flex items-center">
            <span className="font-medium">Cargo</span>
          </div>
        ),
        dataIndex: ["empleado", "cargo"],
        key: "cargo",
        ellipsis: true,
        width: 170,
        responsive: ["md", "lg"],
      },
      {
        title: (
          <div className="flex items-center">
            <span className="font-medium">Dependencia</span>
          </div>
        ),
        dataIndex: ["empleado", "dependencia", "dependencia"],
        key: "dependencia",
        ellipsis: true,
        width: 180,
        responsive: ["lg"],
      },
      {
        title: (
          <div className="flex items-center">
            <span className="font-medium">Tipo Novedad</span>
          </div>
        ),
        dataIndex: ["tipo_novedad", "novedad"],
        key: "tipo_novedad",
        width: 170,
        responsive: ["md", "lg"],
        render: (text) => (
          <Tag color="blue" className="text-center py-1 px-2 font-medium">
            {text}
          </Tag>
        ),
      },
      {
        title: (
          <div className="flex items-center justify-center">
            <span className="font-medium">Mins</span>
          </div>
        ),
        dataIndex: "minutos",
        key: "minutos",
        width: 80,
        align: "center",
        responsive: ["lg"],
      },
      {
        title: (
          <div className="flex items-center justify-center">
            <span className="font-medium">Hrs</span>
          </div>
        ),
        dataIndex: "horas",
        key: "horas",
        width: 80,
        align: "center",
        responsive: ["lg"],
      },
      {
        title: (
          <div className="flex items-center justify-center">
            <span className="font-medium">Días</span>
          </div>
        ),
        dataIndex: "dias",
        key: "dias",
        width: 80,
        align: "center",
        responsive: ["lg"],
      },
      {
        title: (
          <div className="flex items-center justify-center">
            <span className="font-medium">Nómina</span>
          </div>
        ),
        dataIndex: "nomina",
        key: "nomina",
        width: 100,
        align: "center",
        filters: [
          { text: "Sí", value: true },
          { text: "No", value: false },
        ],
        onFilter: (value, record) => record.nomina === value,
        responsive: ["lg"],
        render: (afecta) => (
          <Tag color={afecta ? "success" : "default"} className="mx-auto">
            {afecta ? "SI" : "NO"}
          </Tag>
        ),
      },
      {
        title: (
          <div className="flex items-center">
            <span className="font-medium">Observaciones</span>
          </div>
        ),
        dataIndex: "observaciones",
        key: "observaciones",
        width: 200,
        render: (text) => (
          <Tooltip title={text} placement="topLeft">
            <div className="truncate max-w-xs cursor-help">{text}</div>
          </Tooltip>
        ),
      },
      {
        title: (
          <div className="flex items-center justify-center">
            <span className="font-medium">Soporte</span>
          </div>
        ),
        key: "soporte",
        width: 100,
        align: "center",
        responsive: ["lg"],
        render: (_, record) => (
          <Space size="middle">
            {record.soporte ? (
              <Tooltip title="Ver soporte">
                <Button
                  type="primary"
                  size="small"
                  icon={<FilePdfOutlined />}
                  className="bg-blue-500 hover:bg-blue-700"
                  onClick={() => handleViewSupport(record.id)}
                />
              </Tooltip>
            ) : (
              <Tag color="default">No disponible</Tag>
            )}
          </Space>
        ),
      },
    ]; // Columna de soporte para todos los usuarios

    // Columna de acciones solo para usuarios con permisos
    if (canManageData() || !isReadOnly()) {
      baseColumns.push({
        title: (
          <div className="flex items-center justify-center">
            <span className="font-medium">Acciones</span>
          </div>
        ),
        key: "acciones",
        width: 120,
        align: "center",
        responsive: ["lg"],
        render: (_, record) => (
          <Space size="small">
            {/* Editar - solo admin y administrativo */}
            <AdminAndStaff>
              <Tooltip title="Editar">
                <Button
                  type="primary"
                  size="small"
                  icon={<EditOutlined />}
                  className="bg-yellow-500 hover:bg-yellow-600 border-yellow-500"
                  onClick={() => handleEdit(record)}
                />
              </Tooltip>
            </AdminAndStaff>

            {/* Eliminar - solo admin y administrativo */}
            <AdminAndStaff>
              <Tooltip title="Eliminar">
                <Button
                  type="primary"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(record)}
                />
              </Tooltip>
            </AdminAndStaff>
          </Space>
        ),
      });
    }

    return baseColumns;
  }; // Filtrar datos basado en criterios de búsqueda
  const filteredData = Array.isArray(novedades)
    ? novedades.filter((item) => {
        if (!item) return false; // Validación para evitar errores con objetos null o undefined

        // Validación para búsqueda de texto en varios campos
        let matchesSearch = true;
        if (searchText) {
          matchesSearch =
            item.empleado?.nombre
              ?.toLowerCase()
              .includes(searchText.toLowerCase()) ||
            item.empleado?.cedula
              ?.toLowerCase()
              .includes(searchText.toLowerCase()) ||
            item.observaciones
              ?.toLowerCase()
              .includes(searchText.toLowerCase()) ||
            item.tipo_novedad?.novedad
              ?.toLowerCase()
              .includes(searchText.toLowerCase());
        }

        // Validación de rango de fechas
        let matchesDate = true;
        if (dateRange && dateRange[0] && dateRange[1]) {
          const fechaNovedad = item.fecha_ini ? moment(item.fecha_ini) : null;
          matchesDate =
            fechaNovedad &&
            fechaNovedad.isBetween(dateRange[0], dateRange[1], "day", "[]");
        }

        // Validación de tipo de novedad
        const matchesTipo =
          !tipo ||
          (item.tipo_novedad_id &&
            item.tipo_novedad_id.toString() === tipo.toString());

        // Validación de campus
        const campusValue = item.empleado?.campus?.campus;
        const matchesCampus = !campus || campusValue === campus;

        return matchesSearch && matchesDate && matchesTipo && matchesCampus;
      })
    : [];

  return (
    <div className="p-6">
      {/* Alerta para usuarios con permisos limitados */}
      {isReadOnly() && (
        <Alert
          message="Permisos de Solo Lectura"
          description={`Como usuario ${roleName}, solo puedes visualizar las novedades pero no modificarlas.`}
          type="info"
          showIcon
          className="mb-4"
        />
      )}{" "}
      <Card
        title={
          <div className="flex justify-between items-center">
            <div className="text-2xl font-bold text-gray-800">
              Novedades de Personal
            </div>
            <div className="flex gap-2">
              {/* Botón nuevo - solo para admin y administrativo */}
              <AdminAndStaff>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setEditingNovedad(null);
                    setFormVisible(true);
                  }}
                >
                  Nueva Novedad
                </Button>
              </AdminAndStaff>{" "}
              <Button
                icon={<ReloadOutlined />}
                onClick={cargarDatos}
                loading={loading}
              >
                Actualizar
              </Button>
              {/* Exportar - solo para administradores */}
              <AdminAndCoordinator>
                <Tooltip title="Exportar a Excel">
                  <Button
                    className="flex items-center bg-green-50 hover:bg-green-100 border-green-500 text-green-700"
                    onClick={() => setReporteVisible(true)}
                  >
                    <img
                      src={icono_excel}
                      alt="Excel"
                      className="h-6 mr-1 object-cover"
                    />
                    <span>Exportar</span>
                  </Button>
                </Tooltip>
              </AdminAndCoordinator>
            </div>
          </div>
        }
      >
        {/* Filtros */}
        <div className="mb-4 flex flex-wrap justify-between items-center gap-4">
          <div className="flex flex-wrap gap-4">
            <div>
              <RangePicker
                format="DD/MM/YYYY"
                placeholder={["Fecha inicio", "Fecha fin"]}
                onChange={setDateRange}
                value={dateRange}
                locale={locale}
                className="w-64"
              />
            </div>
            <div>
              <Select
                placeholder="Filtrar por Campus"
                allowClear
                style={{ width: 170 }}
                onChange={(value) => setCampus(value)}
                value={campus}
              >
                {campusOptions.map((campus) => (
                  <Option key={campus} value={campus}>
                    {campus}
                  </Option>
                ))}
              </Select>
            </div>
            <div>
              <Select
                placeholder="Filtrar por Tipo"
                allowClear
                style={{ width: 200 }}
                onChange={setTipo}
                value={tipo}
              >
                {" "}
                {tiposNovedades.map((tipo) => (
                  <Option key={tipo.id} value={tipo.id}>
                    {tipo.novedad}
                  </Option>
                ))}
              </Select>
            </div>
            <div>
              <Input
                placeholder="Buscar..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-60"
                allowClear
              />
            </div>
          </div>

          <div className="flex gap-4">
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                setSearchText("");
                setDateRange([null, null]);
                setCampus(null);
                setTipo(null);
                cargarDatos();
              }}
            >
              Reiniciar filtros
            </Button>
          </div>
        </div>{" "}
        {/* Tabla */}
        <Table
          columns={getTableColumns()}
          dataSource={filteredData}
          loading={loading}
          rowKey="id"
          rowClassName="hover:bg-blue-50 transition-colors cursor-pointer"
          scroll={{ x: 1300, y: "calc(100vh - 350px)" }}
          pagination={{
            total: filteredData.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} novedades`,
          }}
          className="border border-gray-200 rounded-md"
        />
      </Card>{" "}
      {/* Modal de formulario - solo para usuarios con permisos */}{" "}
      <PermissionGuard
        roles={[ROLES.ADMINISTRADOR, ROLES.ADMINISTRATIVO, ROLES.COORDINADOR]}
      >
        {formVisible && (
          <FormNovedad
            visible={formVisible}
            onCancel={() => {
              setFormVisible(false);
              setEditingNovedad(null);
            }}
            onSuccess={() => {
              console.log(
                "[TablaNovedadesConPermisos] Ejecutando callback onSuccess..."
              );
              try {
                setFormVisible(false);
                setEditingNovedad(null);
                console.log(
                  "[TablaNovedadesConPermisos] Modal cerrado y novedad limpiada"
                );
                // Recargar las novedades de forma asíncrona
                cargarDatos().catch((error) => {
                  console.error(
                    "[TablaNovedadesConPermisos] Error al recargar novedades:",
                    error
                  );
                  // No mostrar toast de error aquí para no confundir al usuario
                  // ya que la novedad se creó exitosamente
                });
                console.log(
                  "[TablaNovedadesConPermisos] Recarga de novedades iniciada"
                );
              } catch (error) {
                console.error(
                  "[TablaNovedadesConPermisos] Error en callback onSuccess:",
                  error
                );
              }
            }}
            novedadEditar={editingNovedad}
          />
        )}
      </PermissionGuard>
      {/* Modal de reportes - para todos los usuarios */}
      <ReporteNovedadesModal
        visible={reporteVisible}
        onCancel={() => setReporteVisible(false)}
      />
      {/* Modal de confirmación de eliminación */}
      <DeleteConfirmModal
        visible={deleteModalVisible}
        onConfirm={confirmDelete}
        onCancel={() => {
          setDeleteModalVisible(false);
          setNovedadToDelete(null);
        }}
        title="Eliminar Novedad"
        content={`¿Estás seguro de que deseas eliminar la novedad del empleado ${novedadToDelete?.empleado?.nombre}?`}
      />
    </div>
  );
};

export default TablaNovedadesConPermisos;
